import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
  MapPin, ShieldCheck, Phone, ArrowRight, ChevronDown, ChevronUp,
  Paintbrush2, Palette, X,
} from "lucide-react";
import api, { API } from "@/lib/api";
import usePageMeta from "@/hooks/usePageMeta";
import { usePublicSettings } from "@/hooks/usePublicSettings";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

function absolute(url) {
  if (!url) return null;
  return url.startsWith("http") ? url : `${BACKEND_URL}${url}`;
}

// Block G — LocalBusiness JSON-LD for a single painter profile. No telephone,
// email or aggregateRating (founder-locked privacy posture + no fake reviews).
function buildPainterJsonLd(profile, slug) {
  const img = absolute(profile.card_image_url);
  const areas = (profile.areas_regions || []).map((r) => ({
    "@type": "AdministrativeArea",
    name: r,
  }));
  const awards = (profile.badges || []).map((b) => b.label).filter(Boolean);
  const doc = {
    "@context": "https://schema.org",
    "@type": ["LocalBusiness", "PaintingService"],
    "@id": `https://colourmatch.au/painters/${slug}`,
    name: profile.business_name,
    url: `https://colourmatch.au/painters/${slug}`,
    description: profile.bio || undefined,
    image: img || undefined,
    logo: absolute(profile.logo_url) || undefined,
    address: {
      "@type": "PostalAddress",
      addressLocality: profile.hq_suburb,
      addressRegion: profile.hq_state,
      postalCode: profile.hq_postcode,
      addressCountry: "AU",
    },
    areaServed: areas.length ? areas : undefined,
    award: awards.length ? awards : undefined,
    serviceType: (profile.service_scope || []).map((s) =>
      `${s.charAt(0).toUpperCase()}${s.slice(1)} painting`),
    openingHours: "Mo-Sa 07:00-18:00",
    parentOrganization: {
      "@type": "Organization",
      name: "ColourMatch",
      url: "https://colourmatch.au",
    },
  };
  // Strip undefined keys for a clean payload
  Object.keys(doc).forEach((k) => { if (doc[k] === undefined) delete doc[k]; });
  return doc;
}

async function track(slug, payload) {
  try {
    const body = JSON.stringify(payload);
    const url = `${API}/painters/${encodeURIComponent(slug)}/track`;
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: "application/json" });
      navigator.sendBeacon(url, blob);
      return;
    }
    await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body, keepalive: true,
    });
  } catch { /* swallow */ }
}

function GalleryLightbox({ items, activeIdx, onClose, onNav, slug }) {
  const item = items[activeIdx];
  // Fire gallery_view exactly once per lightbox session (not on prev/next).
  useEffect(() => {
    track(slug, { event: "gallery_view", source: "profile" });
  }, [slug]);
  if (!item) return null;
  return (
    <div
      className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="gallery-lightbox"
    >
      <button
        onClick={onClose}
        data-testid="gallery-close-btn"
        className="absolute top-6 right-6 text-white/80 hover:text-white"
      >
        <X size={28} />
      </button>
      <div
        className="max-w-5xl w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={absolute(item.image_url)}
          alt={item.title}
          className="w-full max-h-[80vh] object-contain rounded-2xl"
        />
        <div className="mt-4 text-center text-white">
          <h3 className="font-semibold text-lg">{item.title}</h3>
          {item.caption && <p className="text-sm text-stone-300 mt-1">{item.caption}</p>}
          <div className="mt-4 flex items-center justify-center gap-3 text-sm">
            <button
              onClick={() => onNav(-1)}
              disabled={activeIdx <= 0}
              data-testid="gallery-prev-btn"
              className="px-4 h-9 rounded-xl border border-white/30 text-white disabled:opacity-30 hover:bg-white/10"
            >
              Previous
            </button>
            <span className="text-stone-400">{activeIdx + 1} / {items.length}</span>
            <button
              onClick={() => onNav(1)}
              disabled={activeIdx >= items.length - 1}
              data-testid="gallery-next-btn"
              className="px-4 h-9 rounded-xl border border-white/30 text-white disabled:opacity-30 hover:bg-white/10"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function PainterPublicProfile() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const fromProject = searchParams.get("from_project");
  const { settings } = usePublicSettings();
  const indexable = !!settings.PAINTERPRO_PROFILES_INDEXABLE;

  const [profile, setProfile] = useState(null);
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState("");
  const [showAllAreas, setShowAllAreas] = useState(false);
  const [lightbox, setLightbox] = useState(null); // index

  usePageMeta({
    title: profile ? `${profile.business_name} — verified painter on ColourMatch Pro` : "Painter — ColourMatch Pro",
    description: profile
      ? `${profile.business_name} is a verified painter on ColourMatch Pro, serving ${profile.hq_suburb}, ${profile.hq_state}. Request a quote and bring your colour vision to life.`
      : "Verified painter profile on ColourMatch Pro.",
    path: `/painters/${slug}`,
    robots: indexable ? undefined : "noindex,follow",
    // Block G — OG/Twitter preview image: cover_gallery → hero → logo
    ogImage: profile ? absolute(profile.card_image_url) || undefined : undefined,
    // Block G — JSON-LD LocalBusiness schema. Phone/email/aggregateRating
    // are intentionally omitted (founder decision: contact stays private until
    // quote; no fake ratings until real reviews exist).
    jsonLd: profile ? buildPainterJsonLd(profile, slug) : null,
  });

  useEffect(() => {
    let alive = true;
    async function go() {
      setBusy(true);
      setErr("");
      try {
        const r = await api.get(`/public/painters/${encodeURIComponent(slug)}`);
        if (!alive) return;
        setProfile(r.data);
        // Fire profile_view once on mount
        track(slug, {
          event: "profile_view",
          source: fromProject ? "workspace_cta" : "directory",
          project_source: fromProject ? "workspace_cta" : null,
        });
      } catch (e) {
        if (!alive) return;
        if (e?.response?.status === 404) {
          setErr("Painter not found.");
        } else {
          setErr("Could not load painter profile.");
        }
      } finally {
        if (alive) setBusy(false);
      }
    }
    go();
    return () => { alive = false; };
  }, [slug, fromProject]);

  if (busy) {
    return (
      <div className="min-h-screen bg-brand-bg flex items-center justify-center text-brand-muted" data-testid="profile-loading">
        Loading painter profile…
      </div>
    );
  }

  if (err || !profile) {
    return (
      <div className="min-h-screen bg-brand-bg">
        <div className="max-w-3xl mx-auto px-5 lg:px-8 py-24 text-center">
          <h1 className="text-3xl font-bold text-brand-ink">{err || "Painter not found"}</h1>
          <p className="mt-3 text-brand-muted">
            The painter you&rsquo;re looking for may no longer be listed.
          </p>
          <Link
            to="/painters"
            data-testid="back-to-directory-btn"
            className="mt-8 inline-flex items-center gap-2 bg-brand-navy hover:bg-brand-navy/90 text-white h-12 px-6 rounded-2xl text-sm font-semibold tap-spring"
          >
            Back to directory <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    );
  }

  const heroImg = absolute(profile.card_image_url) || absolute(profile.hero_image_url) || absolute(profile.logo_url);
  const regions = profile.areas_regions || [];
  const areasDetail = profile.areas_detail || [];
  const gallery = profile.gallery || [];

  const quoteUrl = `/quote?painter=${encodeURIComponent(profile.slug)}${
    fromProject ? `&from_project=${encodeURIComponent(fromProject)}` : ""
  }`;

  return (
    <div className="bg-brand-bg min-h-screen pb-16">
      {/* Hero */}
      <section
        className="relative bg-brand-ink h-[42vh] min-h-[320px] lg:h-[52vh] lg:min-h-[420px]"
        data-testid="profile-hero"
      >
        {heroImg ? (
          <img
            src={heroImg}
            alt={profile.business_name}
            className="absolute inset-0 w-full h-full object-cover opacity-90"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <Paintbrush2 size={48} className="text-white/30" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-black/10" />
        <div className="absolute bottom-0 inset-x-0">
          <div className="max-w-7xl mx-auto px-5 lg:px-8 pb-8 lg:pb-12 text-white">
            <Link
              to="/painters"
              data-testid="back-to-directory-link"
              className="inline-flex items-center gap-1.5 text-xs font-medium text-white/70 hover:text-white"
            >
              ← All painters
            </Link>
            <h1 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-tight" data-testid="profile-business-name">
              {profile.business_name}
            </h1>
            <div className="mt-3 flex items-center gap-2 text-sm text-white/85">
              <MapPin size={14} /> {profile.hq_suburb}, {profile.hq_state} {profile.hq_postcode}
            </div>
            {profile.verified_since_label && (
              <span
                data-testid="verified-since-label"
                className="mt-4 inline-flex items-center gap-1.5 bg-brand-navy text-white text-xs font-semibold px-3 py-1.5 rounded-full"
              >
                <ShieldCheck size={13} /> {profile.verified_since_label}
              </span>
            )}
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 mt-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Left column */}
        <div className="lg:col-span-7 space-y-8">
          {profile.bio && (
            <div className="bg-white rounded-3xl border border-brand-border p-6 lg:p-8" data-testid="profile-bio">
              <h2 className="font-bold text-brand-ink text-lg">About</h2>
              <p className="mt-3 text-brand-muted leading-relaxed whitespace-pre-wrap">{profile.bio}</p>
            </div>
          )}

          {/* Services */}
          {(profile.service_scope?.length > 0 || profile.service_surfaces?.length > 0) && (
            <div className="bg-white rounded-3xl border border-brand-border p-6 lg:p-8" data-testid="profile-services">
              <h2 className="font-bold text-brand-ink text-lg">Services</h2>
              {profile.service_scope?.length > 0 && (
                <div className="mt-3">
                  <p className="text-xs uppercase tracking-wide text-brand-muted font-semibold">Scope</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {profile.service_scope.map((s) => (
                      <span key={s} className="inline-flex items-center bg-stone-100 text-brand-ink text-xs font-medium px-2.5 py-1 rounded-full capitalize">{s}</span>
                    ))}
                  </div>
                </div>
              )}
              {profile.service_surfaces?.length > 0 && (
                <div className="mt-5">
                  <p className="text-xs uppercase tracking-wide text-brand-muted font-semibold">Surfaces</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {profile.service_surfaces.map((s) => (
                      <span key={s} className="inline-flex items-center bg-stone-100 text-brand-ink text-xs font-medium px-2.5 py-1 rounded-full capitalize">{s.replace(/_/g, " ")}</span>
                    ))}
                  </div>
                </div>
              )}
              {profile.property_types?.length > 0 && (
                <div className="mt-5">
                  <p className="text-xs uppercase tracking-wide text-brand-muted font-semibold">Property types</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {profile.property_types.map((s) => (
                      <span key={s} className="inline-flex items-center bg-stone-100 text-brand-ink text-xs font-medium px-2.5 py-1 rounded-full capitalize">{s}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Service Areas — friendly regions first (Amendment #2) */}
          {(regions.length > 0 || areasDetail.length > 0) && (
            <div className="bg-white rounded-3xl border border-brand-border p-6 lg:p-8" data-testid="profile-areas">
              <h2 className="font-bold text-brand-ink text-lg">Service areas</h2>
              {regions.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2" data-testid="profile-regions">
                  {regions.map((r) => (
                    <span key={r} className="inline-flex items-center gap-1 bg-brand-teal/10 text-brand-teal text-sm font-semibold px-3 py-1.5 rounded-full">
                      <MapPin size={12} /> {r}
                    </span>
                  ))}
                </div>
              )}
              {areasDetail.length > 0 && (
                <div className="mt-5">
                  <button
                    onClick={() => setShowAllAreas((v) => !v)}
                    data-testid="toggle-detailed-areas"
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-ink hover:text-brand-teal"
                  >
                    {showAllAreas ? "Hide" : "Show"} all {areasDetail.length} postcodes
                    {showAllAreas ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  </button>
                  {showAllAreas && (
                    <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm" data-testid="areas-detailed-list">
                      {areasDetail.map((a) => (
                        <div key={a.postcode} className="bg-stone-50 rounded-xl px-3 py-2 text-brand-ink">
                          <span className="font-semibold">{a.postcode}</span> {a.suburb}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Gallery */}
          {gallery.length > 0 && (
            <div className="bg-white rounded-3xl border border-brand-border p-6 lg:p-8" data-testid="profile-gallery">
              <h2 className="font-bold text-brand-ink text-lg">Gallery</h2>
              <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3">
                {gallery.map((g, idx) => (
                  <button
                    key={g.id}
                    onClick={() => setLightbox(idx)}
                    data-testid={`gallery-thumb-${idx}`}
                    className="group aspect-square bg-stone-100 rounded-2xl overflow-hidden focus:outline-none focus:ring-2 focus:ring-brand-teal"
                  >
                    <img
                      src={absolute(g.image_url)}
                      alt={g.title}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-[1.05] transition-transform duration-500"
                    />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Recommended Colour Schemes — Amendment #7 placeholder */}
          <div
            className="bg-stone-50 border border-dashed border-brand-border rounded-3xl p-6 lg:p-8"
            data-testid="recommended-schemes-placeholder"
          >
            <div className="flex items-center gap-2 text-brand-teal">
              <Palette size={18} />
              <span className="text-xs uppercase tracking-wide font-bold">Coming soon</span>
            </div>
            <h2 className="mt-3 font-bold text-brand-ink text-lg">Recommended Colour Schemes</h2>
            <p className="mt-2 text-sm text-brand-muted max-w-xl">
              {profile.business_name} will soon be able to share colour schemes they recommend
              for projects like yours — built right inside ColourMatch.
            </p>
          </div>
        </div>

        {/* Right column — sticky CTA */}
        <aside className="lg:col-span-5">
          <div className="lg:sticky lg:top-24 space-y-6">
            <div className="bg-white rounded-3xl border border-brand-border p-6 lg:p-8 shadow-soft" data-testid="profile-cta-card">
              <h3 className="text-xl font-bold text-brand-ink">Request a quote</h3>
              <p className="mt-2 text-sm text-brand-muted">
                We&rsquo;ll forward your photo, surfaces and chosen colours straight to {profile.business_name}.
                Phone and email stay private until they reach out to you.
              </p>
              <Link
                to={quoteUrl}
                data-testid="request-quote-btn"
                className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-brand-navy hover:bg-brand-navy/90 text-white h-12 px-6 rounded-2xl text-sm font-semibold tap-spring"
              >
                Request a quote <ArrowRight size={14} />
              </Link>
              <div className="mt-4 flex items-center gap-2 text-xs text-brand-muted">
                <Phone size={12} /> Contact details stay private until {profile.business_name} replies.
              </div>
            </div>

            {/* Verification block — Amendment #4 */}
            <div className="bg-white rounded-3xl border border-brand-border p-6 lg:p-8" data-testid="profile-verification">
              <h3 className="font-bold text-brand-ink">Verification</h3>
              <p className="mt-2 text-sm text-brand-muted">
                ColourMatch checks business identity, public liability insurance and trade licence
                before listing any painter. Certificates are stored privately and never shown publicly.
              </p>
              {profile.badges?.length > 0 && (
                <div className="mt-4 space-y-2" data-testid="badge-list">
                  {profile.badges.map((b) => (
                    <div key={b.code} className="flex items-center gap-2 bg-stone-50 rounded-xl px-3 py-2">
                      <ShieldCheck size={14} style={{ color: b.colour || "#0C5E4A" }} />
                      <span className="text-sm font-semibold text-brand-ink">{b.label}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Visualiser cross-link */}
            <div className="bg-brand-ink text-white rounded-3xl p-6 lg:p-8" data-testid="visualiser-cross-link-profile">
              <h4 className="font-bold text-lg">Want to see colours on your own walls first?</h4>
              <p className="mt-2 text-sm text-stone-300">
                Try the ColourMatch visualiser — upload a photo, choose any paint colour, see the result in seconds.
              </p>
              <Link
                to="/visualiser/new"
                className="mt-4 inline-flex items-center gap-2 bg-white text-brand-ink h-11 px-5 rounded-2xl text-sm font-semibold tap-spring"
              >
                Try the free visualiser <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </aside>
      </section>

      {/* Gallery lightbox */}
      {lightbox !== null && (
        <GalleryLightbox
          items={gallery}
          activeIdx={lightbox}
          slug={slug}
          onClose={() => setLightbox(null)}
          onNav={(delta) => setLightbox((idx) => {
            const next = idx + delta;
            return next >= 0 && next < gallery.length ? next : idx;
          })}
        />
      )}
    </div>
  );
}
